import {
    Component,
    effect,
    Inject,
    inject,
    OnInit,
    signal,
    WritableSignal
} from "@angular/core";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import {
    CategoriesShareListComponentType,
    ShareListSelectComponent
} from "../share-list-select/share-list-select.component";
import { ShareListComponent } from "../share-list/share-list.component";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef
} from "@angular/material/dialog";
import { ModelsDialogComponent } from "../models-dialog/models-dialog.component";
import { GetUsersContactControllerType } from "@arch/presentation/controllers/get-users-contact.controller";
import { CompanyContactEntity } from "@arch/domain/entity/company-contact.entity";
import { CompanyEntity } from "@arch/domain/entity/company.entity";
import { FormatUtils } from "@arch/_shared/utils/format.utils";
import { AddContactDialogComponent } from "../add-contact-dialog/add-contact-dialog.component";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ReportEntity } from "@arch/domain/entity";

@Component({
    selector: "app-share-email-dialog",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        ShareListSelectComponent,
        ShareListComponent
    ],
    templateUrl: "./share-email-dialog.component.html",
    styleUrl: "./share-email-dialog.component.scss"
})
export class ShareEmailDialogComponent implements OnInit {
    public set categories(value: CategoriesShareListComponentType[]) {
        this.categoriesSignal.set(value);
    }
    public get categories(): CategoriesShareListComponentType[] {
        return this.categoriesSignal();
    }
    public categoriesSignal: WritableSignal<
        CategoriesShareListComponentType[]
    > = signal<CategoriesShareListComponentType[]>([]);

    public chips: string[] = [];
    public contacts: string[] = [];
    public loading: boolean = true;
    public textValue: string = "";

    public infoTooltip: string = `O Dash pode ser acessado sem identificação. Para exigir identificação ao acessar o Dash, vá para a Central de Dash, clique em "Configurações" e ative a opção de identificação.`;

    readonly dialog = inject(MatDialog);
    private readonly eventsService = inject(EventsService);
    public readonly dialogRef = inject(MatDialogRef<ShareEmailDialogComponent>);
    public readonly data = inject<ShareWppDialogDataInputType>(MAT_DIALOG_DATA);

    constructor() {
        effect(() => {
            const categories = this.categoriesSignal();
            const contacts = categories.flatMap((category) =>
                category.contacts
                    .filter((contact) => contact.checked)
                    .map((contact) => `${contact.name}; ${contact.email}`)
            );

            this.contacts = contacts;
        });
    }

    ngOnInit(): void {
        this.categories = [
            {
                _id: "empresa",
                name: "Empresa",
                contacts: []
            },
            {
                _id: "contabilidade",
                name: "Contabilidade",
                contacts: []
            }
        ];

        this.getModeledContactsByCategory();
        this.loading = false;
    }

    public handleChips(): string[] {
        return this.chips
            .filter((c) => FormatUtils.mask("email", c).valid)
            .map((ch) => `${FormatUtils.mask("email", ch).formated}`)
            .concat(this.contacts);
    }

    private getModeledContactsByCategory() {
        this.categories[0].contacts = (
            this.data?.contacts?.companyContacts || []
        ).map((contact: CompanyContactEntity) =>
            contact.getContactShare(false)
        );
        this.categories[1].contacts = (
            this.data?.contacts?.usersContact || []
        ).map((contact: CompanyContactEntity) =>
            contact.getContactShare(false)
        );
    }

    public handleSubmit(): void {
        this.eventsService.send(
            `Dash.${this.data.report.type}.${this.data.report.humanSourceName}.compartilhar.email`
        );
        const contactsSelected = this.categories
            .flatMap((category) => category.contacts)
            .filter((contact) => contact.checked);

        if (!contactsSelected.length) {
            this.eventsService.send(
                `Dash.${this.data.report.type}.${this.data.report.humanSourceName}.compartilhar.email-sem-contato`
            );
        }
        this.dialogRef.close({
            chips: this.chips,
            contacts: contactsSelected
        });
    }

    public onOpenModelEmailDialog() {
        this.dialog.open(ModelsDialogComponent, {
            width: "100%",
            maxWidth: "666px",
            height: "100%",
            maxHeight: "507px",
            data: {
                title: "Compartilhar por E-mail",
                description: "Modelo do e-mail (dados fictícios)",
                image: {
                    src: "/images/model-email.png",
                    height: "365px",
                    width: "490px"
                }
            }
        });
    }

    public onUpdateContact(contact: any): void {
        const user = this.data.contacts?.companyContacts.find(
            (c) => c.props._id === contact._id
        );

        this.dialog.open(AddContactDialogComponent, {
            width: "100%",
            maxWidth: "402px",
            maxHeight: "auto",
            data: {
                category: "company",
                title: `Atualizar Contato (Empresa)`,
                description: "Atualizar as informações do contato.",
                type: "email",
                action: "update",
                contact: user,
                companyId: this.data.companyId,
                accountingId: this.data.accountingId,
                handleListContacts: async () => this.refreshListContacts()
            }
        });
    }

    public onOpenAddContact(category: CategoriesShareListComponentType) {
        this.dialog.open(AddContactDialogComponent, {
            width: "100%",
            maxWidth: "402px",
            maxHeight: "auto",
            data: {
                category: category._id === "empresa" ? "company" : "accounting",
                title: `Cadastrar Contato (${category.name})`,
                description: "Insira abaixo o contato para envio do Dash.",
                type: "email",
                companyId: this.data.companyId,
                accountingId: this.data.accountingId,
                handleListContacts: async () => await this.refreshListContacts()
            }
        });
    }

    private async refreshListContacts(): Promise<void> {
        this.loading = true;
        const contacts = await this.data.handleListContacts();
        this.data.contacts = contacts;
        this.getModeledContactsByCategory();

        this.categories = [...this.categories];
        this.loading = false;
    }

    public handleValue(value: string): void {
        this.textValue = value;
    }
}

type ShareWppDialogDataInputType = {
    contacts: GetUsersContactControllerType;
    companyId: string;
    company?: CompanyEntity;
    accountingId: string;
    handleListContacts: () => Promise<any>;
    report: ReportEntity;
};
